/**
 * SHA-256 hashing for Google Enhanced Conversions.
 *
 * Google requires personal identifiers (email, phone) to be hashed BEFORE being sent.
 * Rules per Google's Enhanced Conversions docs:
 *   - lowercase + trim
 *   - phone in E.164 format (e.g. +972534567890), no spaces, dashes, or parentheses
 *   - email lowercase + trim, no extra normalization (do NOT strip dots from gmail)
 *
 * Uses the browser-native SubtleCrypto API. Returns a 64-char lowercase hex string.
 *
 * In environments without Web Crypto (Jest/jsdom without polyfill, older Node, SSR),
 * returns an empty string instead of throwing. This is safe — Google Ads ignores empty
 * user_data fields, and the conversion still fires. Real browsers always have
 * crypto.subtle, so production traffic is unaffected; only test environments fall
 * through to the no-op path.
 */

function getSubtle() {
  // Using `window` (not globalThis) because CRA's default ESLint config doesn't
  // include globalThis as a known global, and the only consumer is the browser.
  // jsdom provides window.crypto.subtle inconsistently — the null fallback handles
  // that test environment cleanly.
  try {
    if (typeof window !== 'undefined'
        && window.crypto
        && window.crypto.subtle
        && typeof window.crypto.subtle.digest === 'function') {
      return window.crypto.subtle;
    }
  } catch (_) {}
  return null;
}

async function sha256Hex(str) {
  if (!str) return '';
  const subtle = getSubtle();
  if (!subtle) return '';
  try {
    const buf = await subtle.digest('SHA-256', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (_) {
    return '';
  }
}

/**
 * Convert Arabic-Indic digits (٠–٩, U+0660–U+0669) and Persian /
 * Extended-Arabic digits (۰–۹, U+06F0–U+06F9) to ASCII before
 * stripping non-digits — otherwise the lead-capture form for an
 * Arabic-keyboard user would feed Google Ads an empty-hash and the
 * Enhanced Conversion match would silently fail. Returns input
 * unchanged when nothing matches.
 */
function arabicDigitsToAscii(s) {
  return String(s || '')
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
}

/**
 * Normalize a phone string to E.164 and hash it.
 * Accepts inputs like "053-392-0842", "+972 53-392-0842", "0533920842",
 * and the same numbers typed in Arabic-Indic / Persian script.
 * Defaults to Israel country code (+972) if a number starts with 0 or has no country.
 */
export async function hashPhone(raw, defaultCountryCode = '972') {
  if (!raw) return '';
  let digits = arabicDigitsToAscii(raw).replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) {
    // already has a country code — keep as-is
  } else if (digits.startsWith('00')) {
    digits = '+' + digits.slice(2);
  } else if (digits.startsWith('0')) {
    // Israeli local format: 0XXXXXXXXX → +972XXXXXXXXX
    digits = '+' + defaultCountryCode + digits.slice(1);
  } else {
    // assume already has country code, missing +
    digits = '+' + digits;
  }
  return sha256Hex(digits);
}

/** Lowercase + trim an email, then hash. */
export async function hashEmail(raw) {
  if (!raw) return '';
  return sha256Hex(String(raw).trim().toLowerCase());
}

/** Lowercase + trim a generic string field (name, address) and hash. */
export async function hashString(raw) {
  if (!raw) return '';
  return sha256Hex(String(raw).trim().toLowerCase());
}
